/**
 * Leistungs- und Preiskatalog im internen Bereich.
 *
 * Ticket SK7 hat die Maske geteilt:
 *  - Anlegen verlangt fünf Angaben (Bezeichnung, Kurzbeschreibung, Preis,
 *    Preisart, Kategorie). Kennung und öffentliche Adresse entstehen dabei von
 *    selbst aus der Bezeichnung.
 *  - Alles Weitere erscheint erst beim Bearbeiten.
 *
 * Zwei Regeln sind bewusst gesetzt:
 *  1. Kursangaben werden bei einer Nicht-Kurs-Kategorie nur ausgeblendet, nie
 *     stillschweigend geleert. Geleert wird ausschließlich nach ausdrücklicher
 *     Rückfrage, die benennt, was verloren geht.
 *  2. Es gibt kein Feld für ein Produkt beim Zahlungsdienst mehr: Der
 *     Bezahlvorgang übergibt Bezeichnung und Preis direkt aus dem Katalog.
 */

import { useMemo, useState } from "react";
import { createFileRoute } from "@tanstack/react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { toast } from "sonner";
import { Loader2, Pencil, Plus, PowerOff, RotateCcw, Trash2, X } from "lucide-react";
import { AdminUploadButton } from "@/components/admin/AdminUploadButton";

import { useAdminSession } from "@/hooks/use-admin-session";
import { supabase } from "@/integrations/supabase/client";
import { sanitizeStorageFilename } from "@/lib/storage-filename";
import { MarkdownEditor } from "@/components/MarkdownEditor.lazy";
import { OfferPeopleManager } from "@/components/admin/OfferPeopleManager";
import { confirmDialog } from "@/lib/confirm-dialog";
import {
  createService,
  listCourseOptions,
  listServices,
  setServiceActive,
  updateService,
  type Service,
} from "@/lib/services.functions";
import {
  SERVICE_CATEGORIES,
  SERVICE_CATEGORY_LABELS,
  SERVICE_COURSE_FORMAT_HELP,
  SERVICE_COURSE_FORMAT_LABELS,
  SERVICE_COURSE_LINK_HELP,
  SERVICE_DURATION_HELP,
  SERVICE_PRICE_KINDS,
  SERVICE_PRICE_KIND_LABELS,
  SERVICE_PURCHASABILITIES,
  SERVICE_PURCHASABILITY_HELP,
  SERVICE_PURCHASABILITY_LABELS,
  VISIBLE_IN_COCKPIT_HELP,
  centsToEuroInput,
  formatServicePrice,
  parseEuroInputToCents,
  validateServiceSalesFields,
  type ServiceCategory,
  type ServicePriceKind,
  type ServicePurchasability,
} from "@/lib/service-format";
import {
  COURSE_PAGE_PREFIX,
  COURSE_PUBLIC_SLUG_HELP,
  COURSE_PUBLIC_SLUG_LOCKED_MESSAGE,
  coursePublicSlugIssue,
  deriveCourseSlug,
} from "@/lib/courses-public";
import {
  SERVICE_KEY_HELP,
  SERVICE_KEY_MAX_LENGTH,
  serviceKeySchema,
  suggestServiceKey,
} from "@/lib/product-cards";

export const Route = createFileRoute("/_admin/admin/services")({
  head: () => ({
    meta: [
      { title: "Leistungen – Admin – creategy" },
      { name: "robots", content: "noindex, nofollow" },
    ],
  }),
  component: ServicesAdminPage,
});

type FormState = {
  id?: string;
  key: string;
  /** Wurde die Kennung von Hand angefasst? Sonst folgt sie der Bezeichnung. */
  keyTouched: boolean;
  title: string;
  short_description: string;
  priceInput: string;
  price_kind: ServicePriceKind;
  billing_note: string;
  category: ServiceCategory;
  sort_order: string;
  visible_in_cockpit: boolean;
  public_on_landing: boolean;
  // Ticket SK1: Verkaufsangaben.
  course_id: string;
  duration_label: string;
  purchasability: ServicePurchasability;
  // Ticket SK5/SK7: öffentliche Kursseite.
  public_slug: string;
  /** Adresse bewusst abweichend gesetzt? Sonst gilt die Vorschau. */
  slugTouched: boolean;
  public_slug_locked: boolean;
  long_description: string;
  seo_title: string;
  seo_description: string;
  // Ticket SK8: Produktbild im eigenen Ablagebereich `service-images`.
  image_path: string | null;
};

/** Ticket SK8: eigener Ablagebereich der Produktbilder. */
const SERVICE_IMAGE_BUCKET = "service-images";

const emptyForm: FormState = {
  key: "",
  keyTouched: false,
  title: "",
  short_description: "",
  priceInput: "",
  price_kind: "festpreis",
  billing_note: "",
  category: "amtszeit_modul",
  sort_order: "0",
  visible_in_cockpit: false,
  public_on_landing: false,
  course_id: "",
  duration_label: "",
  purchasability: "anfragbar",
  public_slug: "",
  slugTouched: false,
  public_slug_locked: false,
  long_description: "",
  seo_title: "",
  seo_description: "",
  image_path: null,
};

function toForm(row: Service): FormState {
  return {
    id: row.id,
    key: row.key ?? "",
    keyTouched: true,
    title: row.title,
    short_description: row.short_description,
    priceInput: centsToEuroInput(row.price_cents),
    price_kind: row.price_kind,
    billing_note: row.billing_note ?? "",
    category: row.category,
    sort_order: String(row.sort_order),
    visible_in_cockpit: row.visible_in_cockpit,
    public_on_landing: row.public_on_landing ?? false,
    course_id: row.course_id ?? "",
    duration_label: row.duration_label ?? "",
    purchasability: row.purchasability ?? "anfragbar",
    public_slug: row.public_slug ?? "",
    slugTouched: !!row.public_slug,
    public_slug_locked: row.public_slug_locked_at !== null,
    long_description: row.long_description ?? "",
    seo_title: row.seo_title ?? "",
    seo_description: row.seo_description ?? "",
    image_path: row.image_path ?? null,
  };
}

/** Aus der Bezeichnung abgeleitete Adresse, solange keine eigene gesetzt ist. */
function effectiveSlug(form: FormState): string {
  if (form.slugTouched || form.public_slug) return form.public_slug.trim().toLowerCase();
  return deriveCourseSlug(form.title);
}

const inputClass =
  "mt-1 w-full rounded-md border border-border bg-background px-3 py-2 text-sm outline-none focus:border-primary";
const labelClass = "block text-sm font-semibold text-foreground";

function ServicesAdminPage() {
  const session = useAdminSession();
  const isAdmin = session.isAdmin;
  const qc = useQueryClient();

  const listFn = useServerFn(listServices);
  const createFn = useServerFn(createService);
  const updateFn = useServerFn(updateService);
  const activeFn = useServerFn(setServiceActive);
  const courseOptionsFn = useServerFn(listCourseOptions);

  const [categoryFilter, setCategoryFilter] = useState<ServiceCategory | "">("");
  const [statusFilter, setStatusFilter] = useState<"all" | "active" | "inactive">("all");
  const [form, setForm] = useState<FormState | null>(null);
  const [errors, setErrors] = useState<Record<string, string>>({});

  const { data: rows, isLoading, error } = useQuery({
    queryKey: ["admin-services", { categoryFilter, statusFilter }],
    queryFn: () =>
      listFn({
        data: {
          category: categoryFilter || null,
          ...(statusFilter === "all" ? {} : { activeOnly: statusFilter === "active" }),
        },
      }),
  });

  // Ticket SK1: Kursauswahl für den Abschnitt „Verkauf".
  const { data: courseOptions } = useQuery({
    queryKey: ["admin-service-course-options"],
    queryFn: () => courseOptionsFn({}),
    enabled: isAdmin,
  });

  const groups = useMemo(() => {
    const list = rows ?? [];
    return SERVICE_CATEGORIES.map((cat) => ({
      category: cat,
      items: list.filter((r) => r.category === cat),
    })).filter((g) => g.items.length > 0);
  }, [rows]);

  const isEditing = !!form?.id;
  const isCourseCategory = form?.category === "kurs";

  const saveMutation = useMutation({
    mutationFn: async (state: FormState) => {
      const cents = parseEuroInputToCents(state.priceInput);
      if (cents === null) throw new Error("Preis ungültig");

      // Ticket SK7: Anlegen mit fünf Angaben; die Kennung entsteht automatisch.
      if (!state.id) {
        return createFn({
          data: {
            key: state.key.trim(),
            title: state.title.trim(),
            short_description: state.short_description.trim(),
            price_cents: cents,
            price_kind: state.price_kind,
            category: state.category,
            // Ticket SK8: Kursverknüpfung bereits beim Anlegen, optional.
            course_id: state.category === "kurs" && state.course_id ? state.course_id : null,
          },
        });
      }

      const slug = effectiveSlug(state);
      return updateFn({
        data: {
          id: state.id,
          key: state.key.trim(),
          title: state.title.trim(),
          short_description: state.short_description.trim(),
          price_cents: cents,
          price_kind: state.price_kind,
          billing_note: state.billing_note.trim() || null,
          category: state.category,
          sort_order: Number(state.sort_order) || 0,
          visible_in_cockpit: state.visible_in_cockpit,
          public_on_landing: state.public_on_landing,
          // Ticket SK1: Verkaufsangaben. Ticket SK7: Kursangaben bleiben auch
          // dann erhalten, wenn die Kategorie gerade keine Kurskategorie ist —
          // sie werden nur ausgeblendet.
          course_id: state.course_id || null,
          duration_label: state.duration_label.trim() || null,
          purchasability: state.purchasability,
          // Eine gesperrte Adresse wird unverändert mitgesendet, die Datenbank
          // lehnt jede Änderung ohnehin ab.
          public_slug: slug || null,
          long_description: state.long_description.trim() || null,
          seo_title: state.seo_title.trim() || null,
          seo_description: state.seo_description.trim() || null,
          image_path: state.image_path,
        },
      });
    },
    onSuccess: (result: unknown) => {
      // Ticket Z12: Beim Ändern der Kennung die betroffenen Kapitel benennen.
      const affected = (result as { affected_chapters?: Array<{ chapterNumber: number; title: string }> } | null)
        ?.affected_chapters;
      if (affected && affected.length > 0) {
        toast.warning(
          "Kennung geändert. Bitte in diesen Kapiteln nachbessern: " +
            affected.map((c) => `Kapitel ${c.chapterNumber} – ${c.title}`).join("; "),
          { duration: 15000 },
        );
      }
      toast.success("Leistung gespeichert.");
      setForm(null);
      setErrors({});
      qc.invalidateQueries({ queryKey: ["admin-services"] });
    },
    onError: (e: unknown) => {
      const message = e instanceof Error ? e.message : "";
      if (message.includes("Forbidden")) {
        toast.error("Nur Administratoren dürfen Leistungen ändern.");
        return;
      }
      // Ticket SK1: Der Grund der Sperre wird im Wortlaut der Serverfunktion gezeigt.
      toast.error(message || "Leistung konnte nicht gespeichert werden.", {
        duration: 12000,
      });
    },
  });

  const activeMutation = useMutation({
    mutationFn: (vars: { id: string; active: boolean }) => activeFn({ data: vars }),
    onSuccess: (_r, vars) => {
      toast.success(vars.active ? "Leistung reaktiviert." : "Leistung deaktiviert.");
      qc.invalidateQueries({ queryKey: ["admin-services"] });
    },
    onError: () => toast.error("Status konnte nicht geändert werden."),
  });

  /** Ticket SK7: bereits vergebene Kennungen – ohne den offenen Eintrag. */
  const takenKeys = useMemo(
    () =>
      (rows ?? [])
        .filter((r) => r.id !== form?.id)
        .map((r) => (r.key ?? "").toLowerCase()),
    [rows, form?.id],
  );

  /** Ticket SK5: bereits belegte öffentliche Adressen – ohne den offenen Eintrag. */
  const takenPublicSlugs = useMemo(
    () =>
      (rows ?? [])
        .filter((r) => r.id !== form?.id && r.public_slug)
        .map((r) => r.public_slug as string),
    [rows, form?.id],
  );

  function keyIssue(state: FormState): string | null {
    const value = state.key.trim().toLowerCase();
    const parsed = serviceKeySchema.safeParse(value);
    if (!parsed.success) return parsed.error.issues[0]?.message ?? "Kennung ungültig.";
    if (takenKeys.includes(value)) {
      return `„${value}“ ist bereits von einer anderen Leistung belegt.`;
    }
    return null;
  }

  function submit() {
    if (!form) return;
    const next: Record<string, string> = {};
    if (!form.title.trim()) next.title = "Bezeichnung ist erforderlich.";
    const keyProblem = keyIssue(form);
    if (keyProblem) next.key = keyProblem;
    if (!form.short_description.trim())
      next.short_description = "Kurzbeschreibung ist erforderlich.";
    else if (form.short_description.trim().length > 400)
      next.short_description =
        "Die Kurzbeschreibung darf höchstens 400 Zeichen umfassen.";
    const cents = parseEuroInputToCents(form.priceInput);
    if (cents === null)
      next.priceInput = "Bitte einen Betrag in Euro angeben, etwa 2.500,50.";

    if (form.id) {
      // Ticket SK1: dieselbe Regel wie in der Serverfunktion, nur früher sichtbar.
      const salesProblem = validateServiceSalesFields({
        purchasability: form.purchasability,
        course_id: form.course_id || null,
      });
      if (salesProblem) next.purchasability = salesProblem;

      // Ticket SK5: Kollision der öffentlichen Adresse sofort melden. Pflicht ist
      // sie erst, wenn der Kurs kaufbar gesetzt wird.
      const slug = effectiveSlug(form);
      if (slug || (form.category === "kurs" && form.purchasability === "kaufbar")) {
        const slugProblem = coursePublicSlugIssue(slug, takenPublicSlugs);
        if (slugProblem) next.public_slug = slugProblem;
      }
    }

    setErrors(next);
    if (Object.keys(next).length > 0) return;
    saveMutation.mutate(form);
  }

  /** Kursauswahl: bereits verknüpfte Kurse sind erkennbar und nicht wählbar. */
  const courseChoices = (courseOptions ?? []).map((c) => ({
    ...c,
    disabled: c.linkedServiceId !== null && c.linkedServiceId !== form?.id,
  }));

  /** Ticket W15: Format des verknüpften Kurses, nur zur Anzeige. */
  const linkedCourseFormatLabel = (() => {
    const linked = (courseOptions ?? []).find((c) => c.id === form?.course_id);
    return linked ? SERVICE_COURSE_FORMAT_LABELS[linked.courseFormat] : null;
  })();

  const previewCents = form ? parseEuroInputToCents(form.priceInput) : null;

  /** Welche Kursangaben trägt der Eintrag, obwohl die Kategorie keine Kurskategorie ist? */
  const hiddenCourseValues = useMemo(() => {
    if (!form || form.category === "kurs") return [] as string[];
    const parts: string[] = [];
    if (form.course_id) parts.push("Kursverknüpfung");
    if (form.duration_label.trim()) parts.push("Dauer");
    if (form.long_description.trim()) parts.push("ausführlicher Text");
    if (form.seo_title.trim() || form.seo_description.trim()) parts.push("Suchmaschinenangaben");
    if (form.public_slug.trim() && !form.public_slug_locked) parts.push("öffentliche Adresse");
    return parts;
  }, [form]);

  // Ticket SK8: Produktbild in den eigenen Ablagebereich legen. Das alte Objekt
  // verschwindet mit, damit keine Waisen zurückbleiben.
  const [imageUploading, setImageUploading] = useState(false);

  const { data: imagePreviewUrl } = useQuery({
    queryKey: ["admin-service-image", form?.image_path ?? null],
    enabled: !!form?.image_path,
    queryFn: async () => {
      const { data, error } = await supabase.storage
        .from(SERVICE_IMAGE_BUCKET)
        .createSignedUrl(form!.image_path as string, 60 * 60);
      if (error) return null;
      return data?.signedUrl ?? null;
    },
  });

  async function onImageUpload(file: File) {
    if (!form) return;
    setImageUploading(true);
    try {
      const path = `${crypto.randomUUID()}/${sanitizeStorageFilename(file.name)}`;
      const { error } = await supabase.storage
        .from(SERVICE_IMAGE_BUCKET)
        .upload(path, file, { upsert: false, contentType: file.type });
      if (error) throw new Error(error.message);
      const previous = form.image_path;
      setForm({ ...form, image_path: path });
      if (previous) await supabase.storage.from(SERVICE_IMAGE_BUCKET).remove([previous]);
      toast.success("Produktbild hinterlegt. Bitte noch speichern.");
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Upload fehlgeschlagen.");
    } finally {
      setImageUploading(false);
    }
  }

  async function onImageRemove() {
    if (!form?.image_path) return;
    const previous = form.image_path;
    setForm({ ...form, image_path: null });
    await supabase.storage.from(SERVICE_IMAGE_BUCKET).remove([previous]);
    toast.success("Produktbild entfernt. Bitte noch speichern.");
  }

  async function clearCourseFields() {
    if (!form) return;
    const ok = await confirmDialog({
      title: "Kursangaben wirklich leeren?",
      description:
        `Gelöscht werden: ${hiddenCourseValues.join(", ")}. ` +
        (form.public_slug_locked
          ? "Die veröffentlichte Adresse bleibt erhalten, weil sie außerhalb verlinkt sein kann. "
          : "") +
        "Das lässt sich nicht rückgängig machen.",
      confirmLabel: "Kursangaben leeren",
    });
    if (!ok) return;
    setForm({
      ...form,
      course_id: "",
      duration_label: "",
      long_description: "",
      seo_title: "",
      seo_description: "",
      // Eine veröffentlichte Adresse bleibt bestehen, sie kann außerhalb verlinkt sein.
      public_slug: form.public_slug_locked ? form.public_slug : "",
      slugTouched: form.public_slug_locked,
    });
    toast.success("Kursangaben geleert. Bitte noch speichern.");
  }

  return (
    <div className="mx-auto max-w-6xl">
      <header className="mb-8 flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="font-display text-3xl font-extrabold tracking-tight text-primary">
            Leistungen
          </h1>
          <p className="mt-2 max-w-2xl text-sm text-muted-foreground">
            Zentraler Leistungs- und Preiskatalog. Preise werden in Cent geführt und
            überall einheitlich dargestellt. Leistungen werden nie gelöscht, sondern
            nur deaktiviert.
            {!isAdmin && " Als Editorin oder Editor können Sie den Katalog nur ansehen."}
          </p>
        </div>
        {isAdmin && (
          <button
            type="button"
            onClick={() => {
              setForm({ ...emptyForm });
              setErrors({});
            }}
            className="inline-flex items-center gap-2 rounded-md bg-brand-button px-4 py-2 text-sm font-semibold text-brand-button-foreground"
          >
            <Plus className="h-4 w-4" /> Neue Leistung
          </button>
        )}
      </header>

      <div className="mb-6 flex flex-wrap items-end gap-4">
        <div>
          <label className={labelClass} htmlFor="filter-category">
            Kategorie
          </label>
          <select
            id="filter-category"
            value={categoryFilter}
            onChange={(e) => setCategoryFilter(e.target.value as ServiceCategory | "")}
            className={inputClass}
          >
            <option value="">Alle Kategorien</option>
            {SERVICE_CATEGORIES.map((c) => (
              <option key={c} value={c}>
                {SERVICE_CATEGORY_LABELS[c]}
              </option>
            ))}
          </select>
        </div>
        <div>
          <label className={labelClass} htmlFor="filter-status">
            Status
          </label>
          <select
            id="filter-status"
            value={statusFilter}
            onChange={(e) =>
              setStatusFilter(e.target.value as "all" | "active" | "inactive")
            }
            className={inputClass}
          >
            <option value="all">Alle</option>
            <option value="active">Nur aktive</option>
            <option value="inactive">Nur deaktivierte</option>
          </select>
        </div>
      </div>

      {isLoading ? (
        <p className="text-sm text-muted-foreground">Leistungen werden geladen …</p>
      ) : error ? (
        <p className="text-sm text-destructive">
          Der Katalog konnte nicht geladen werden.
        </p>
      ) : groups.length === 0 ? (
        <p className="text-sm text-muted-foreground">
          Für diese Auswahl sind keine Leistungen vorhanden.
        </p>
      ) : (
        <div className="space-y-8">
          {groups.map((group) => (
            <section key={group.category}>
              <h2 className="font-display text-lg font-extrabold text-foreground">
                {SERVICE_CATEGORY_LABELS[group.category]}
              </h2>
              <ul className="mt-3 divide-y divide-border rounded-lg border border-border bg-card">
                {group.items.map((row) => (
                  <li key={row.id} className="flex flex-wrap items-start gap-4 px-5 py-4">
                    <div className="min-w-0 flex-1">
                      <p className="font-display text-base font-extrabold text-foreground">
                        {row.title}
                        {!row.active && (
                          <span className="ml-2 rounded bg-muted px-2 py-0.5 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
                            deaktiviert
                          </span>
                        )}
                      </p>
                      <p className="mt-1 text-sm text-muted-foreground">
                        {row.short_description}
                      </p>
                      <p className="mt-2 text-sm text-foreground">
                        <span className="font-semibold">
                          {formatServicePrice(row.price_cents, row.price_kind)}
                        </span>
                        {row.billing_note && (
                          <span className="text-muted-foreground"> · {row.billing_note}</span>
                        )}
                        <span className="text-muted-foreground">
                          {" "}
                          · {SERVICE_PRICE_KIND_LABELS[row.price_kind]}
                        </span>
                      </p>
                      <p className="mt-1 text-xs text-muted-foreground">
                        Kennung: <code className="font-mono">{row.key}</code>
                      </p>
                      <p className="mt-1 text-xs text-muted-foreground">
                        Cockpit-Sichtbarkeit:{" "}
                        {row.visible_in_cockpit ? "vorgesehen" : "nicht vorgesehen"} ·
                        Sortierung {row.sort_order}
                      </p>
                    </div>
                    {isAdmin && (
                      <div className="flex shrink-0 gap-2">
                        <button
                          type="button"
                          onClick={() => {
                            setForm(toForm(row));
                            setErrors({});
                          }}
                          className="inline-flex items-center gap-1.5 rounded-md border border-border px-3 py-1.5 text-xs font-semibold"
                        >
                          <Pencil className="h-3.5 w-3.5" /> Bearbeiten
                        </button>
                        <button
                          type="button"
                          disabled={activeMutation.isPending}
                          onClick={() =>
                            activeMutation.mutate({ id: row.id, active: !row.active })
                          }
                          className="inline-flex items-center gap-1.5 rounded-md border border-border px-3 py-1.5 text-xs font-semibold"
                        >
                          {row.active ? (
                            <>
                              <PowerOff className="h-3.5 w-3.5" /> Deaktivieren
                            </>
                          ) : (
                            <>
                              <RotateCcw className="h-3.5 w-3.5" /> Reaktivieren
                            </>
                          )}
                        </button>
                      </div>
                    )}
                  </li>
                ))}
              </ul>
            </section>
          ))}
        </div>
      )}

      {isAdmin && form && (
        <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-black/40 p-4">
          <div className="mt-8 w-full max-w-2xl rounded-lg border border-border bg-card p-6 shadow-xl">
            <div className="mb-4 flex items-start justify-between gap-4">
              <div>
                <h2 className="font-display text-xl font-extrabold text-primary">
                  {isEditing ? "Leistung bearbeiten" : "Neue Leistung"}
                </h2>
                {!isEditing && (
                  <p className="mt-1 text-xs text-muted-foreground">
                    Fünf Angaben genügen. Format, Dauer, Kursverknüpfung, Sichtbarkeiten
                    und die öffentliche Kursseite folgen beim Bearbeiten.
                  </p>
                )}
              </div>
              <button
                type="button"
                aria-label="Formular schließen"
                onClick={() => setForm(null)}
                className="rounded-md border border-border p-1.5"
              >
                <X className="h-4 w-4" />
              </button>
            </div>

            <div className="space-y-4">
              <div>
                <label className={labelClass} htmlFor="svc-title">
                  Bezeichnung *
                </label>
                <input
                  id="svc-title"
                  value={form.title}
                  onChange={(e) => {
                    const title = e.target.value;
                    setForm({
                      ...form,
                      title,
                      // Ticket SK7: Kennung folgt der Bezeichnung, bis jemand sie anfasst.
                      key: form.keyTouched ? form.key : suggestServiceKey(title),
                    });
                  }}
                  className={inputClass}
                />
                {errors.title && (
                  <p className="mt-1 text-xs text-destructive">{errors.title}</p>
                )}
              </div>

              <div>
                <label className={labelClass} htmlFor="svc-desc">
                  Kurzbeschreibung *
                </label>
                <textarea
                  id="svc-desc"
                  rows={2}
                  value={form.short_description}
                  onChange={(e) =>
                    setForm({ ...form, short_description: e.target.value })
                  }
                  maxLength={400}
                  className={inputClass}
                />
                <p className="mt-1 text-xs text-muted-foreground">
                  Ein bis zwei Sätze. {form.short_description.trim().length}/400 Zeichen.
                </p>
                {errors.short_description && (
                  <p className="mt-1 text-xs text-destructive">
                    {errors.short_description}
                  </p>
                )}
              </div>

              {/* Ticket SK7: Dauer gehört zu den beschreibenden Angaben, nur bei Kursen. */}
              {isCourseCategory && (
                <div>
                  <label className={labelClass} htmlFor="svc-duration">
                    Dauer
                  </label>
                  <input
                    id="svc-duration"
                    value={form.duration_label}
                    onChange={(e) => setForm({ ...form, duration_label: e.target.value })}
                    className={inputClass}
                    placeholder="4 Stunden"
                    disabled={!isEditing}
                  />
                  <p className="mt-1 text-xs text-muted-foreground">
                    {SERVICE_DURATION_HELP}
                  </p>
                </div>
              )}

              <div className="grid gap-4 sm:grid-cols-2">
                <div>
                  <label className={labelClass} htmlFor="svc-price">
                    Preis in Euro *
                  </label>
                  <input
                    id="svc-price"
                    inputMode="decimal"
                    value={form.priceInput}
                    onChange={(e) => setForm({ ...form, priceInput: e.target.value })}
                    className={inputClass}
                    placeholder="2.500,50"
                  />
                  {errors.priceInput ? (
                    <p className="mt-1 text-xs text-destructive">{errors.priceInput}</p>
                  ) : (
                    <p className="mt-1 text-xs text-muted-foreground">
                      Anzeige:{" "}
                      {previewCents === null
                        ? "—"
                        : formatServicePrice(previewCents, form.price_kind)}
                    </p>
                  )}
                </div>
                <div>
                  <label className={labelClass} htmlFor="svc-kind">
                    Preisart *
                  </label>
                  <select
                    id="svc-kind"
                    value={form.price_kind}
                    onChange={(e) =>
                      setForm({ ...form, price_kind: e.target.value as ServicePriceKind })
                    }
                    className={inputClass}
                  >
                    {SERVICE_PRICE_KINDS.map((k) => (
                      <option key={k} value={k}>
                        {SERVICE_PRICE_KIND_LABELS[k]}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="grid gap-4 sm:grid-cols-2">
                <div>
                  <label className={labelClass} htmlFor="svc-cat">
                    Kategorie *
                  </label>
                  <select
                    id="svc-cat"
                    value={form.category}
                    onChange={(e) =>
                      setForm({ ...form, category: e.target.value as ServiceCategory })
                    }
                    className={inputClass}
                  >
                    {SERVICE_CATEGORIES.map((c) => (
                      <option key={c} value={c}>
                        {SERVICE_CATEGORY_LABELS[c]}
                      </option>
                    ))}
                  </select>
                </div>
                {isEditing && (
                  <div>
                    <label className={labelClass} htmlFor="svc-note">
                      Abrechnungshinweis
                    </label>
                    <input
                      id="svc-note"
                      value={form.billing_note}
                      onChange={(e) => setForm({ ...form, billing_note: e.target.value })}
                      className={inputClass}
                      placeholder="monatlich, einmalig, je Termin"
                    />
                  </div>
                )}
              </div>

              {/* Ticket SK7: Kennung entsteht automatisch, bleibt aber sichtbar. */}
              <div className="rounded-md border border-border bg-muted/30 p-4">
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <p className="text-sm font-semibold text-foreground">
                    Kennung:{" "}
                    <code className="font-mono text-primary">{form.key || "—"}</code>
                  </p>
                  {!form.keyTouched && (
                    <button
                      type="button"
                      onClick={() => setForm({ ...form, keyTouched: true })}
                      className="text-xs font-semibold text-primary underline"
                    >
                      Kennung anpassen
                    </button>
                  )}
                </div>
                {form.keyTouched && (
                  <input
                    id="svc-key"
                    aria-label="Kennung"
                    value={form.key}
                    maxLength={SERVICE_KEY_MAX_LENGTH}
                    onChange={(e) =>
                      setForm({
                        ...form,
                        key: e.target.value.toLowerCase().replace(/\s+/g, "-"),
                      })
                    }
                    className={`${inputClass} font-mono`}
                    placeholder="rathaus-basis"
                  />
                )}
                <p className="mt-1 text-xs text-muted-foreground">{SERVICE_KEY_HELP}</p>
                {errors.key && <p className="mt-1 text-xs text-destructive">{errors.key}</p>}

                {/* Öffentliche Adresse: Vorschau, nur bei Kursen von Belang. */}
                {isCourseCategory && (
                  <div className="mt-3 border-t border-border pt-3">
                    <div className="flex flex-wrap items-center justify-between gap-2">
                      <p className="text-sm font-semibold text-foreground">
                        Öffentliche Adresse:{" "}
                        <code className="font-mono text-primary">
                          {COURSE_PAGE_PREFIX}/{effectiveSlug(form) || "…"}
                        </code>
                      </p>
                      {!form.public_slug_locked && !form.slugTouched && isEditing && (
                        <button
                          type="button"
                          onClick={() =>
                            setForm({
                              ...form,
                              slugTouched: true,
                              public_slug: effectiveSlug(form),
                            })
                          }
                          className="text-xs font-semibold text-primary underline"
                        >
                          Adresse anpassen
                        </button>
                      )}
                    </div>
                    {form.slugTouched && !form.public_slug_locked && (
                      <input
                        id="svc-public-slug"
                        aria-label="Öffentliche Adresse"
                        value={form.public_slug}
                        onChange={(e) =>
                          setForm({ ...form, public_slug: e.target.value.toLowerCase() })
                        }
                        className={`${inputClass} font-mono`}
                        placeholder="social-media-trends"
                      />
                    )}
                    {errors.public_slug ? (
                      <p className="mt-1 text-xs text-destructive">{errors.public_slug}</p>
                    ) : (
                      <p className="mt-1 text-xs text-muted-foreground">
                        {form.public_slug_locked
                          ? COURSE_PUBLIC_SLUG_LOCKED_MESSAGE
                          : COURSE_PUBLIC_SLUG_HELP}
                      </p>
                    )}
                  </div>
                )}
              </div>

              {/* Ticket SK7: Hinweis auf ausgeblendete, aber erhaltene Kursangaben. */}
              {isEditing && hiddenCourseValues.length > 0 && (
                <div className="rounded-md border border-brand-orange/40 bg-brand-orange/10 p-4">
                  <p className="text-sm text-foreground">
                    Diese Leistung trägt noch Kursangaben ({hiddenCourseValues.join(", ")}).
                    Sie sind in dieser Kategorie ausgeblendet, bleiben aber erhalten und
                    werden beim Speichern nicht verändert.
                  </p>
                  <button
                    type="button"
                    onClick={clearCourseFields}
                    className="mt-2 rounded-md border border-border bg-background px-3 py-1.5 text-xs font-semibold"
                  >
                    Kursangaben leeren
                  </button>
                </div>
              )}

              {/* Ticket SK8: Kursverknüpfung schon beim Anlegen. */}
              {isCourseCategory && !isEditing && (
                <div>
                  <label className={labelClass} htmlFor="svc-course-create">
                    Kursverknüpfung
                  </label>
                  <select
                    id="svc-course-create"
                    value={form.course_id}
                    onChange={(e) => setForm({ ...form, course_id: e.target.value })}
                    className={inputClass}
                  >
                    <option value="">Keine Kursverknüpfung</option>
                    {courseChoices.map((c) => (
                      <option key={c.id} value={c.id} disabled={c.disabled}>
                        {c.title}
                        {c.status === "draft" ? " (Entwurf)" : ""}
                        {c.disabled ? ` – bereits verknüpft mit ${c.linkedServiceTitle}` : ""}
                      </option>
                    ))}
                  </select>
                  <p className="mt-1 text-xs text-muted-foreground">
                    {SERVICE_COURSE_LINK_HELP}
                  </p>
                </div>
              )}

              {isEditing && (
                <>
                  {/* Ticket SK8: Produktbild, ein Bild je Leistung. */}
                  <section className="rounded-md border border-border p-4">
                    <h3 className="font-display text-base font-extrabold text-primary">
                      Produktbild
                    </h3>
                    <p className="mt-1 text-xs text-muted-foreground">
                      Ein Bild je Leistung, freigestellt oder mit ruhigem Hintergrund. Es
                      erscheint in der öffentlichen Übersicht und auf der Kursseite. Ohne
                      Bild bleibt dort eine ruhige Platzhalterfläche.
                    </p>

                    <div className="mt-4 flex flex-wrap items-start gap-4">
                      <div className="aspect-[16/10] w-56 overflow-hidden rounded-sm border border-border bg-muted/30">
                        {form.image_path && imagePreviewUrl ? (
                          <img
                            src={imagePreviewUrl}
                            alt=""
                            className="h-full w-full object-contain"
                          />
                        ) : null}
                      </div>
                      <div>
                        <AdminUploadButton
                          label={form.image_path ? "Bild ersetzen" : "Bild hochladen"}
                          accept="image/*"
                          ariaLabel="Produktbild auswählen"
                          uploading={imageUploading}
                          currentName={form.image_path ? form.image_path.split("/").pop() : null}
                          hint="JPG, PNG oder WebP."
                          onFiles={(files) => {
                            const file = files[0];
                            if (file) void onImageUpload(file);
                          }}
                        >
                          {form.image_path ? (
                            <button
                              type="button"
                              onClick={onImageRemove}
                              className="inline-flex items-center gap-2 rounded-md border border-destructive/40 px-3 py-2 text-xs font-semibold text-destructive hover:bg-destructive/10"
                            >
                              <Trash2 className="h-3.5 w-3.5" aria-hidden /> Entfernen
                            </button>
                          ) : null}
                        </AdminUploadButton>
                      </div>

                    </div>
                  </section>

                  <div className="grid gap-4 sm:grid-cols-2">
                    <div>
                      <label className={labelClass} htmlFor="svc-sort">
                        Sortierung
                      </label>
                      <input
                        id="svc-sort"
                        inputMode="numeric"
                        value={form.sort_order}
                        onChange={(e) =>
                          setForm({ ...form, sort_order: e.target.value.replace(/\D/g, "") })
                        }
                        className={inputClass}
                      />
                    </div>
                    <div className="flex items-start gap-3 pt-6">
                      <input
                        id="svc-visible"
                        type="checkbox"
                        checked={form.visible_in_cockpit}
                        onChange={(e) =>
                          setForm({ ...form, visible_in_cockpit: e.target.checked })
                        }
                        className="mt-1 h-4 w-4"
                      />
                      <label htmlFor="svc-visible" className="text-sm">
                        <span className="font-semibold">Sichtbar im Kundencockpit</span>
                        <span className="mt-1 block text-xs text-muted-foreground">
                          {VISIBLE_IN_COCKPIT_HELP}
                        </span>
                      </label>
                    </div>
                    <div className="flex items-start gap-3">
                      <input
                        id="svc-public-landing"
                        type="checkbox"
                        checked={form.public_on_landing}
                        onChange={(e) =>
                          setForm({ ...form, public_on_landing: e.target.checked })
                        }
                        className="mt-1 h-4 w-4"
                      />
                      <label htmlFor="svc-public-landing" className="text-sm">
                        <span className="font-semibold">
                          Auf der öffentlichen Weiterbildungsseite anzeigen
                        </span>
                        <span className="mt-1 block text-xs text-muted-foreground">
                          Erscheint dann unter /100-tage/weiterbildung zur Auswahl. Nur
                          aktive Leistungen werden dort angezeigt.
                        </span>
                      </label>
                    </div>
                  </div>

                  {/* Ticket SK1: Verkaufsangaben je Katalogeintrag. */}
                  <section className="rounded-lg border border-border bg-muted/30 p-4">
                    <h3 className="font-display text-base font-extrabold text-primary">
                      Verkauf
                    </h3>
                    <p className="mt-1 text-xs text-muted-foreground">
                      Bezeichnung und Preis werden beim Kauf unmittelbar aus diesem Katalog
                      übergeben. Ein Produkt beim Zahlungsdienst ist nicht nötig.
                    </p>

                    <div className="mt-4 space-y-4">
                      <div>
                        <label className={labelClass} htmlFor="svc-purchasability">
                          Kaufbarkeit *
                        </label>
                        <select
                          id="svc-purchasability"
                          value={form.purchasability}
                          onChange={(e) =>
                            setForm({
                              ...form,
                              purchasability: e.target.value as ServicePurchasability,
                            })
                          }
                          className={inputClass}
                        >
                          {SERVICE_PURCHASABILITIES.map((p) => (
                            <option key={p} value={p}>
                              {SERVICE_PURCHASABILITY_LABELS[p]}
                            </option>
                          ))}
                        </select>
                        {errors.purchasability ? (
                          <p className="mt-1 text-xs text-destructive">
                            {errors.purchasability}
                          </p>
                        ) : (
                          <p className="mt-1 text-xs text-muted-foreground">
                            {SERVICE_PURCHASABILITY_HELP}
                          </p>
                        )}
                      </div>

                      {isCourseCategory && (
                        <div className="grid gap-4 sm:grid-cols-2">
                          <div>
                            <label className={labelClass} htmlFor="svc-course">
                              Kursverknüpfung
                            </label>
                            <select
                              id="svc-course"
                              value={form.course_id}
                              onChange={(e) =>
                                setForm({ ...form, course_id: e.target.value })
                              }
                              className={inputClass}
                            >
                              <option value="">Keine Kursverknüpfung</option>
                              {courseChoices.map((c) => (
                                <option key={c.id} value={c.id} disabled={c.disabled}>
                                  {c.title}
                                  {c.status === "draft" ? " (Entwurf)" : ""}
                                  {c.disabled
                                    ? ` – bereits verknüpft mit ${c.linkedServiceTitle}`
                                    : ""}
                                </option>
                              ))}
                            </select>
                            <p className="mt-1 text-xs text-muted-foreground">
                              {SERVICE_COURSE_LINK_HELP}
                            </p>
                          </div>
                          <div>
                            <span className={labelClass}>Format</span>
                            {/* Ticket W15: Das Format wird am Kurs gepflegt und
                                hier nur angezeigt – es gibt genau eine Quelle. */}
                            <p className="mt-1 rounded-md border border-input bg-secondary/40 px-3 py-2 text-sm text-foreground">
                              {linkedCourseFormatLabel ?? "Ohne Kursverknüpfung"}
                            </p>
                            <p className="mt-1 text-xs text-muted-foreground">
                              {SERVICE_COURSE_FORMAT_HELP}
                            </p>
                          </div>
                        </div>
                      )}
                    </div>
                  </section>

                  {/* Ticket SK5: öffentliche Kursseite unter /wissen/kurse/. */}
                  {isCourseCategory && (
                    <section className="rounded-md border border-border p-4">
                      <h3 className="font-display text-base font-extrabold text-primary">
                        Öffentliche Kursseite
                      </h3>
                      <p className="mt-1 text-xs text-muted-foreground">
                        Gilt für Kurse, die kaufbar sind. Die Adresse steht oben bei der
                        Kennung.
                      </p>

                      <div className="mt-4 space-y-4">
                        <div>
                          <label className={labelClass} htmlFor="svc-long-description">
                            Ausführlicher Text
                          </label>
                          {/* Ticket SK8: derselbe Editor wie in Kapiteln, Cases,
                              Blog und Lead-Magneten – nichts neu gebaut. */}
                          <div className="mt-1">
                            <MarkdownEditor
                              value={form.long_description}
                              onChange={(v) => setForm({ ...form, long_description: v })}
                              rows={14}
                            />
                          </div>
                          <p className="mt-1 text-xs text-muted-foreground">
                            Formatierung mit Markdown (Überschriften, Listen,
                            Hervorhebungen). Bleibt das Feld leer, zeigt die Kursseite nur
                            die Kurzbeschreibung. Preise nicht in den Text schreiben – sie
                            kommen automatisch aus dem Katalog.
                          </p>
                        </div>

                        <div className="grid gap-4 sm:grid-cols-2">
                          <div>
                            <label className={labelClass} htmlFor="svc-seo-title">
                              Titel für Suchmaschinen
                            </label>
                            <input
                              id="svc-seo-title"
                              value={form.seo_title}
                              onChange={(e) => setForm({ ...form, seo_title: e.target.value })}
                              className={inputClass}
                            />
                            <p className="mt-1 text-xs text-muted-foreground">
                              Ohne Eintrag wird die Bezeichnung verwendet.
                            </p>
                          </div>
                          <div>
                            <label className={labelClass} htmlFor="svc-seo-description">
                              Beschreibung für Suchmaschinen
                            </label>
                            <input
                              id="svc-seo-description"
                              value={form.seo_description}
                              onChange={(e) =>
                                setForm({ ...form, seo_description: e.target.value })
                              }
                              className={inputClass}
                            />
                            <p className="mt-1 text-xs text-muted-foreground">
                              Ohne Eintrag wird die Kurzbeschreibung verwendet.
                            </p>
                          </div>
                        </div>


                        {/* Ticket W11: Personen erscheinen als Karten auf der
                            Kursseite. Die Zuordnung speichert sofort und hängt
                            deshalb nicht am Speicherknopf dieses Editors. */}
                        <div className="border-t border-border pt-4">
                          <OfferPeopleManager
                            offer={form.id ? { kind: "service", id: form.id } : null}
                          />
                        </div>
                      </div>
                    </section>
                  )}
                </>
              )}
            </div>

            <div className="mt-6 flex justify-end gap-3">
              <button
                type="button"
                onClick={() => setForm(null)}
                className="rounded-md border border-border px-4 py-2 text-sm font-semibold"
              >
                Abbrechen
              </button>
              <button
                type="button"
                onClick={submit}
                disabled={saveMutation.isPending}
                className="inline-flex items-center gap-2 rounded-md bg-brand-button px-4 py-2 text-sm font-semibold text-brand-button-foreground disabled:opacity-60"
              >
                {saveMutation.isPending && <Loader2 className="h-4 w-4 animate-spin" />}
                Speichern
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
